/**
 * Validator — slotting-core
 * Pure validation logic for droplet configurations: resource totals,
 * capacity constraints, homogeneous slotting, and NUMA bin-packing.
 * Ported from OutpostForge v1 js/validator.js with the DOM code removed and
 * the global AppState.generation replaced by an explicit parameter.
 */

import { NUMA_CONFIG } from './catalog.js';
import type {
  BarColor,
  Droplet,
  DropletValidationResult,
  FamilyData,
  NumaConfig,
  NumaValidationResult,
  ResourceTotals,
} from './types.js';

/**
 * Calculate total resource consumption for a droplet.
 */
export function calculateTotals(
  droplet: Droplet | null | undefined,
  familyData: FamilyData | null | undefined,
): ResourceTotals {
  let vCPUs = 0;
  let memoryGiB = 0;
  let gpus = 0;

  if (!droplet || !familyData || !droplet.instances) {
    return { vCPUs, memoryGiB, gpus };
  }

  for (const [typeName, count] of Object.entries(droplet.instances)) {
    if (count <= 0) continue;
    const spec = familyData.sizes.find((s) => s.name === typeName);
    if (spec) {
      vCPUs += spec.vCPUs * count;
      memoryGiB += spec.memoryGiB * count;
      gpus += (spec.gpus ?? 0) * count;
    }
  }

  return { vCPUs, memoryGiB, gpus };
}

/**
 * Count total number of instances in a droplet.
 */
export function countInstances(droplet: Droplet | null | undefined): number {
  if (!droplet || !droplet.instances) return 0;
  let total = 0;
  for (const count of Object.values(droplet.instances)) {
    if (count > 0) total += count;
  }
  return total;
}

/**
 * Check if instances can be distributed across NUMA nodes without crossing boundaries.
 * The generation's NUMA topology is looked up in `numaConfigMap` (defaults to NUMA_CONFIG);
 * generations absent from the map skip NUMA validation entirely.
 *
 * Two-phase bin-packing algorithm:
 *   Phase 1: Separate instances into node-spanning (vCPUs > nodeSize) and
 *            node-constrained (vCPUs <= nodeSize). Node-spanning instances are
 *            exempt from the NUMA constraint.
 *   Phase 2: Calculate reduced per-node capacity by deducting node-spanning vCPUs
 *            proportionally across all nodes.
 *   Phase 3: Sort node-constrained instances by vCPU descending, use first-fit
 *            decreasing to place each into nodes with the reduced capacity.
 */
export function validateNUMA(
  droplet: Droplet,
  familyData: FamilyData,
  generation: string,
  numaConfigMap: Record<string, NumaConfig> = NUMA_CONFIG,
): NumaValidationResult {
  const config = numaConfigMap[generation];
  if (!config) return { valid: true, message: '' };

  const { nodeCount, vCPUsPerNode: nodeSize } = config;

  // Phase 1: Separate instances into node-spanning and node-constrained
  let spanningVCPUs = 0;
  const constrainedList: number[] = [];
  for (const [typeName, count] of Object.entries(droplet.instances)) {
    if (count <= 0) continue;
    const spec = familyData.sizes.find((s) => s.name === typeName);
    if (!spec) continue;
    for (let j = 0; j < count; j++) {
      if (spec.vCPUs > nodeSize) {
        // Node-spanning: exempt from NUMA constraint, deduct proportionally
        spanningVCPUs += spec.vCPUs;
      } else {
        // Node-constrained: must fit within a single node
        constrainedList.push(spec.vCPUs);
      }
    }
  }

  // If no constrained instances, all instances are spanning (or none) — valid
  if (constrainedList.length === 0) return { valid: true, message: '' };

  const violationMessage =
    'NUMA violation: instances cannot be distributed across ' +
    nodeCount +
    ' NUMA nodes (' +
    nodeSize +
    ' vCPUs each)';

  // Phase 2: Calculate reduced per-node capacity after node-spanning deduction
  const effectiveNodeCapacity = nodeSize - spanningVCPUs / nodeCount;

  // If effective capacity is zero or negative, no room for constrained instances
  if (effectiveNodeCapacity <= 0) {
    return { valid: false, message: violationMessage };
  }

  // Phase 3: Sort node-constrained instances descending, first-fit decreasing
  constrainedList.sort((a, b) => b - a);

  const nodes: number[] = new Array<number>(nodeCount).fill(0);

  for (const vcpu of constrainedList) {
    let placed = false;
    for (let m = 0; m < nodeCount; m++) {
      if ((nodes[m] ?? 0) + vcpu <= effectiveNodeCapacity) {
        nodes[m] = (nodes[m] ?? 0) + vcpu;
        placed = true;
        break;
      }
    }
    if (!placed) {
      return { valid: false, message: violationMessage };
    }
  }

  return { valid: true, message: '' };
}

/**
 * Validate a droplet configuration against its family capacity:
 * homogeneous slotting, resource limits, and NUMA distribution.
 */
export function validateDroplet(
  droplet: Droplet,
  familyData: FamilyData,
  generation: string,
  numaConfigMap: Record<string, NumaConfig> = NUMA_CONFIG,
): DropletValidationResult {
  const totals = calculateTotals(droplet, familyData);
  const capacity = familyData.dropletCapacity;

  // Check active instance types (count > 0)
  const activeTypes = Object.entries(droplet.instances).filter(([, count]) => count > 0);

  // Homogeneous slotting violation
  const homogeneousViolation = familyData.homogeneousOnly && activeTypes.length > 1;
  if (homogeneousViolation) {
    return {
      valid: false,
      fullyOccupied: false,
      slotsBreached: true,
      combinationPossible: false,
      totals,
      capacity,
      homogeneousViolation: true,
      message: 'Esta familia solo permite slotting homogéneo',
    };
  }

  const memoryExceeded = totals.memoryGiB > capacity.memoryGiB;
  const vcpuExceeded = totals.vCPUs > capacity.vCPUs;
  const gpuExceeded = capacity.gpus > 0 && totals.gpus > capacity.gpus;
  let slotsBreached = memoryExceeded || vcpuExceeded || gpuExceeded;

  const fullyOccupied =
    !slotsBreached &&
    totals.memoryGiB === capacity.memoryGiB &&
    totals.vCPUs === capacity.vCPUs;

  let combinationPossible = !slotsBreached && activeTypes.length > 0;

  // NUMA node validation (generation-aware via numaConfigMap)
  const numaResult = validateNUMA(droplet, familyData, generation, numaConfigMap);
  const numaViolation = !numaResult.valid;
  if (numaViolation) {
    slotsBreached = true;
    combinationPossible = false;
  }

  return {
    valid: !slotsBreached,
    fullyOccupied,
    slotsBreached,
    combinationPossible,
    totals,
    capacity,
    homogeneousViolation: false,
    numaViolation,
    message: numaViolation
      ? numaResult.message
      : slotsBreached
        ? 'La configuración excede la capacidad del droplet'
        : '',
  };
}

/**
 * Determine the color for a resource bar based on usage percentage.
 * green <80%, yellow 80-99%, red >=100%
 */
export function getBarColorClass(pct: number): BarColor {
  if (pct >= 100) return 'red';
  if (pct >= 80) return 'yellow';
  return 'green';
}
